// frontend/src/components/ContentManagerPages/BlogForm.js
import React, { useMemo, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import "./BlogForm.css";
import { createBlog, getImages, updateBlog } from "../../services/blogService";
import { getBlogById } from "../../services/servicesblogs";
 
const VIDEO_FILE_PATTERN = /\.(mp4|webm|ogg|mov|m4v|avi)(\?.*)?$/i;

const EDIT_BLOG_STORAGE_KEY = "content-manager-edit-blog";

// Default form state used for new blog creation.
const blank = {
  title: "",
  slug: "",
  excerpt: "",
  metaTitle: "",
  metaDescription: "",
  content: "",
  category: "Technology",
  status: true,
  featuredImage: [],
};

// Converts checkbox-style or string status values into API-friendly labels.
const toStatusValue = (status) =>
  String(status).toUpperCase() === "PUBLISHED" || status === true ? "PUBLISHED" : "DRAFT";

// Normalizes different API response wrappers after create/update.
const unwrapBlogResponse = (response) => {
  const payload = response?.data ?? response;
  return payload?.blog ?? payload?.data ?? payload ?? null;
};

const inferMediaType = (value = "") =>
  VIDEO_FILE_PATTERN.test(value) ? "video" : "image";

const normalizeMediaItem = (item) => {
  const src =
    item?.url ??
    item?.fileUrl ??
    item?.secure_url ??
    item?.imageUrl ??
    item?.src ??
    "";

  return {
    id: item?.id || item?._id || src || `${Date.now()}-${Math.random()}`,
    name: item?.name || item?.filename || src.split("/").pop() || "uploaded-file",
    src,
    type: item?.type || inferMediaType(src),
  };
};

// Shared blog form used for both create and edit flows.
export default function BlogForm({ existingBlog, onSave, images }) {
  const location = useLocation();
  const navigate = useNavigate();
  const outlet = useOutletContext();
  const isEditRoute = location.pathname.endsWith("/blogposts/edit");
  const storedEditBlog = useMemo(() => {
    if (!isEditRoute) return null;
    try {
      const raw = sessionStorage.getItem(EDIT_BLOG_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [isEditRoute]);

  const resolvedExistingBlog = useMemo(
    () => existingBlog ?? (isEditRoute ? outlet?.editingBlog || location.state?.blog || storedEditBlog : null),
    [existingBlog, isEditRoute, location.state?.blog, outlet?.editingBlog, storedEditBlog]
  );
  const [apiImages, setApiImages] = useState([]);
  const resolvedImages = useMemo(() => {
    if (Array.isArray(images) && images.length) return images;
    if (Array.isArray(outlet?.images) && outlet.images.length) return outlet.images;
    return apiImages;
  }, [images, outlet?.images, apiImages]);
  const resolvedOnSave = onSave ?? (isEditRoute ? outlet?.handleUpdateBlog : outlet?.handleCreateBlog);

  const [formData, setFormData] = useState(blank);
  const [slugId] = useState(() => Date.now());
  const [imageInput, setImageInput] = useState("");
  const [errors, setErrors] = useState({});
  const [fetchedEditBlog, setFetchedEditBlog] = useState(null);

  const editBlogId = useMemo(
    () =>
      resolvedExistingBlog?.id ||
      resolvedExistingBlog?._id ||
      location.state?.blog?.id ||
      location.state?.blog?._id ||
      storedEditBlog?.id ||
      storedEditBlog?._id ||
      null,
    [resolvedExistingBlog, location.state?.blog, storedEditBlog]
  );

  useEffect(() => {
    const loadImages = async () => {
      try {
        const payload = await getImages();
        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.data?.data)
            ? payload.data.data
            : Array.isArray(payload?.images)
              ? payload.images
              : [];
        const normalized = list.map(normalizeMediaItem);
        setApiImages(normalized.filter((img) => img.name && img.src));
      } catch {
        setApiImages([]);
      }
    };
    loadImages();
  }, []);

  useEffect(() => {
    if (!isEditRoute || !editBlogId) {
      setFetchedEditBlog(null);
      return;
    }

    const loadEditBlog = async () => {
      try {
        const response = await getBlogById(editBlogId);
        const payload = response?.data ?? response;
        const blog = payload?.blog ?? payload?.data ?? payload ?? null;
        setFetchedEditBlog(blog);
      } catch {
        setFetchedEditBlog(null);
      }
    };

    loadEditBlog();
  }, [editBlogId, isEditRoute]);

  const hydratedExistingBlog = useMemo(
    () =>
      fetchedEditBlog
        ? { ...(resolvedExistingBlog || {}), ...fetchedEditBlog }
        : resolvedExistingBlog,
    [fetchedEditBlog, resolvedExistingBlog]
  );

  useEffect(() => {
    if (hydratedExistingBlog) {
      const sourceImages =
        hydratedExistingBlog.featuredImage ??
        hydratedExistingBlog.featured_image ??
        [];
      const selectedImages = Array.isArray(sourceImages)
        ? sourceImages
        : sourceImages
          ? [sourceImages]
          : [];
      const nextFormData = {
        ...blank,
        ...hydratedExistingBlog,
        metaTitle: hydratedExistingBlog.metaTitle || hydratedExistingBlog.meta_title || "",
        metaDescription:
          hydratedExistingBlog.metaDescription ||
          hydratedExistingBlog.meta_description ||
          hydratedExistingBlog.excerpt ||
          "",
        featuredImage: selectedImages,
      };

      setFormData((prev) => {
        const prevKey = JSON.stringify(prev);
        const nextKey = JSON.stringify(nextFormData);
        return prevKey === nextKey ? prev : nextFormData;
      });
      setImageInput((prev) => (prev === (selectedImages[0] || "") ? prev : (selectedImages[0] || "")));
      setErrors((prev) => (Object.keys(prev).length ? {} : prev));
    } else {
      setFormData((prev) => (JSON.stringify(prev) === JSON.stringify(blank) ? prev : blank));
      setImageInput((prev) => (prev === "" ? prev : ""));
      setErrors((prev) => (Object.keys(prev).length ? {} : prev));
    }
  }, [hydratedExistingBlog]);

  useEffect(() => {
    if (!isEditRoute) {
      sessionStorage.removeItem(EDIT_BLOG_STORAGE_KEY);
      return;
    }

    if (hydratedExistingBlog) {
      sessionStorage.setItem(EDIT_BLOG_STORAGE_KEY, JSON.stringify(hydratedExistingBlog));
    }
  }, [hydratedExistingBlog, isEditRoute]);

  useEffect(() => {
    if (hydratedExistingBlog) return;

    const base = formData.title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

    setFormData((prev) => ({
      ...prev,
      slug: base ? `${base}-${slugId}` : "",
    }));
  }, [formData.title, hydratedExistingBlog, slugId]);

  const selectedImagePreviews = useMemo(() => {
    const selected = Array.isArray(formData.featuredImage) ? formData.featuredImage : [];
    return selected
      .map((token) => {
        const matchedMedia = resolvedImages.find(
          (img) =>
            img.name === token ||
            img.id === token ||
            img.src === token ||
            img.url === token
        );

        if (matchedMedia) {
          return normalizeMediaItem(matchedMedia);
        }

        if (typeof token === "string" && token.trim()) {
          return {
            id: token,
            name: token.split("/").pop() || "selected-media",
            src: token,
            type: inferMediaType(token),
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [formData.featuredImage, resolvedImages]);

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleStatusChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, status: value === "PUBLISHED" }));
  };

  const handleFeaturedImageChange = (e) => {
    setImageInput(e.target.value);
    setErrors((prev) => {
      if (!prev.featuredImage) return prev;
      const next = { ...prev };
      delete next.featuredImage;
      return next;
    });
  };

  const addFeaturedImage = () => {
    const value = imageInput.trim();
    if (!value) return;
    setFormData((prev) => {
      const selected = Array.isArray(prev.featuredImage) ? prev.featuredImage : [];
      if (selected.includes(value)) return prev;
      return { ...prev, featuredImage: [...selected, value] };
    });
    setImageInput("");
    setErrors((prev) => {
      if (!prev.featuredImage) return prev;
      const next = { ...prev };
      delete next.featuredImage;
      return next;
    });
  };

  const removeFeaturedImage = (tokenToRemove) => {
    setFormData((prev) => ({
      ...prev,
      featuredImage: (prev.featuredImage || []).filter((img) => img !== tokenToRemove),
    }));
    setImageInput((prev) => (prev === tokenToRemove ? "" : prev));
  };

  // Validates the form, normalizes payloads, and submits create or update requests.
  const submit = async (e) => {
    e.preventDefault();
    const title = formData.title?.trim();
    const content = formData.content?.trim();
    const metaTitle = formData.metaTitle?.trim() || "";
    const metaDescription = formData.metaDescription?.trim() || "";
    const nextErrors = {};

    if (!title) {
      nextErrors.title = "Title is required.";
    } else if (title.length < 3 || title.length > 100) {
      nextErrors.title = "Title must be between 3 and 100 characters.";
    }

    if (!content) {
      nextErrors.content = "Content is required.";
    } else if (content.length < 100) {
      nextErrors.content = "Content must be at least 100 characters.";
    }

    if (metaTitle && (metaTitle.length < 10 || metaTitle.length > 50)) {
      nextErrors.metaTitle = "Meta title must be between 10 and 50 characters.";
    }

    const selectedTokens = Array.isArray(formData.featuredImage) ? formData.featuredImage : [];
    const effectiveTokens = isEditRoute
      ? selectedTokens
      : selectedTokens.length
        ? selectedTokens
        : imageInput
          ? [imageInput]
          : [];
    const selectedImages = effectiveTokens
      .map((token) =>
        resolvedImages.find(
          (img) =>
            img.name === token ||
            img.id === token ||
            img.src === token ||
            img.url === token
        )
      )
      .filter(Boolean);
    const featuredImageUrls = selectedImages
      .map((img) => img?.src || img?.url)
      .filter((url) => typeof url === "string" && /^https?:\/\//i.test(url));
    const selectedImageNames = selectedImages
      .map((img) => img?.name)
      .filter(Boolean);

    if (metaDescription && (metaDescription.length < 30 || metaDescription.length > 300)) {
      nextErrors.metaDescription = "Meta description must be between 30 and 300 characters.";
    }

    if (effectiveTokens.length === 0 || selectedImageNames.length === 0) {
      nextErrors.featuredImage = "Required image.";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const normalizedPayload = {
      title,
      content,
      status: toStatusValue(formData.status),
      ...(isEditRoute
        ? {
            // In edit mode, always send SEO fields so clearing values persists.
            meta_title: metaTitle,
            meta_description: metaDescription,
          }
        : {
            ...(metaTitle && { meta_title: metaTitle }),
            ...(metaDescription && { meta_description: metaDescription }),
          }),
    };

    if (isEditRoute) {
      try {
        const blogId = hydratedExistingBlog?.id || hydratedExistingBlog?._id;
        if (!blogId) {
          window.alert("Blog ID not found for update.");
          return;
        }

        const updatePayload = {
          ...normalizedPayload,
          ...(selectedImageNames.length ? { imageNames: selectedImageNames } : {}),
          ...(featuredImageUrls.length ? { featured_image: featuredImageUrls } : {}),
        };

        if (resolvedOnSave) {
          await resolvedOnSave(updatePayload);
          navigate("/ContentManager/ContentManagerLayout/blogposts");
        } else {
          const response = await updateBlog(blogId, updatePayload);
          const savedBlog = unwrapBlogResponse(response);
          navigate("/ContentManager/ContentManagerLayout/blogposts", {
            state: {
              latestBlog: {
                ...(hydratedExistingBlog || {}),
                ...updatePayload,
                ...(savedBlog || {}),
                id: blogId,
                _id: blogId,
                featured_image: (savedBlog?.featured_image ?? updatePayload.featured_image ?? []),
                content: savedBlog?.content ?? updatePayload.content,
                meta_description: savedBlog?.meta_description ?? updatePayload.meta_description,
                meta_title: savedBlog?.meta_title ?? updatePayload.meta_title,
              },
            },
          });
        }
      } catch (error) {
        const message = error?.response?.data?.message || "Failed to update post.";
        window.alert(message);
        console.error("Error updating blog:", error?.response?.data || error?.message);
      }
      return;
    }

    try {
      const createPayload = {
        ...normalizedPayload,
        ...(selectedImageNames.length ? { imageNames: selectedImageNames } : {}),
        ...(featuredImageUrls.length ? { featured_image: featuredImageUrls } : {}),
      };
      const response = await createBlog(createPayload);
      const savedBlog = unwrapBlogResponse(response);
      navigate("/ContentManager/ContentManagerLayout/blogposts", {
        state: {
          latestBlog: {
            ...createPayload,
            ...(savedBlog || {}),
            featured_image: savedBlog?.featured_image ?? createPayload.featured_image ?? [],
            content: savedBlog?.content ?? createPayload.content,
            meta_description: savedBlog?.meta_description ?? createPayload.meta_description,
            meta_title: savedBlog?.meta_title ?? createPayload.meta_title,
          },
        },
      });
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to create post.";
      window.alert(message);
      console.error("Error creating blog:", error?.response?.data || error?.message);
    }
  };

  if (isEditRoute && !hydratedExistingBlog) {
    return <Navigate to=".." replace />;
  }

  return (
    <form className="blog-editor" onSubmit={submit}>
      <div className="editor-head">
        <h1>{hydratedExistingBlog ? "Edit Blog Post" : "Create New Blog Post"}</h1>
      </div>

      <section className="editor-main card">
        <label>
          Title <span className="required">*</span>
        </label>
        <input
          name="title"
          value={formData.title}
          onChange={change}
          placeholder="Enter blog title..."
          className={errors.title ? "field-error" : ""}
          required
        />
        {errors.title ? <p className="field-error-text">{errors.title}</p> : null}

        <label>
          Content <span className="required">*</span>
        </label>
        <textarea
          name="content"
          rows={9}
          value={formData.content}
          onChange={change}
          placeholder="Write your blog content here..."
          className={errors.content ? "field-error" : ""}
          required
        />
        {errors.content ? <p className="field-error-text">{errors.content}</p> : null}

        <label>Status</label>
        <select value={toStatusValue(formData.status)} onChange={handleStatusChange}>
          <option value="DRAFT">DRAFT</option>
          <option value="PUBLISHED">PUBLISHED</option>
        </select>

        <label>Meta Title</label>
        <input
          name="metaTitle"
          value={formData.metaTitle}
          onChange={change}
          placeholder="SEO Title"
          className={errors.metaTitle ? "field-error" : ""}
        />
        {errors.metaTitle ? <p className="field-error-text">{errors.metaTitle}</p> : null}

        <label>Meta Description</label>
        <textarea
          name="metaDescription"
          rows={4}
          value={formData.metaDescription}
          onChange={change}
          placeholder="SEO description"
          className={errors.metaDescription ? "field-error" : ""}
        />
        {errors.metaDescription ? <p className="field-error-text">{errors.metaDescription}</p> : null}

        <label>Featured Image</label>
        <div className="featured-image-row">
          <select value={imageInput} onChange={handleFeaturedImageChange}>
            <option value="">Choose image</option>
            {resolvedImages.map((img) => (
              <option key={img.id} value={img.src || img.url || img.name}>
                {img.name}
              </option>
            ))}
          </select>
          <button type="button" className="image-add-btn" onClick={addFeaturedImage} disabled={!imageInput}>
            Add
          </button>
        </div>
        {errors.featuredImage ? <p className="field-error-text">{errors.featuredImage}</p> : null}

        {selectedImagePreviews.length > 0 && (
          <div className="multi-image-preview-grid">
            {selectedImagePreviews.map((img) => (
              <div key={img.id} className="single-image-preview">
                {img.type === "video" ? (
                  <video controls preload="metadata">
                    <source src={img.src} />
                  </video>
                ) : (
                  <img src={img.src} alt={img.name} />
                )}
                <button
                  type="button"
                  className="image-remove-btn"
                  onClick={() => removeFeaturedImage(img.src || img.url || img.name || img.id)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        <button type="submit" className="submit-post-btn">
          {hydratedExistingBlog ? "Update Post" : "Create Post"}
        </button>
      </section>
    </form>
  );
}
 
